import { log } from '../app/logger';
import { loadSettings } from '../app/settingsB';
import { config } from '../config';

/**
 * B spec §2.4 — **the lock-nudge minute, read as a number** (build unit B-19a).
 *
 * LOCK-1 has said *"from a **configurable** minute, default `LOCK_NUDGE_MIN`"* since requirements
 * revision 1, and BAK-5 has carried a `lockNudgeMin` setting since U-5. Until this unit nothing
 * read it: `domain/chrome.lockNudgeDue` took no threshold, so B-19 declined to build the control
 * §2.4 lists rather than ship a setting whose value changed nothing a user could observe, and
 * flagged the engine gap under requirements §5 clause 5. Andrew ruled: *"I'm fine with the engine
 * extension for this specific purpose."* This module is B's half of that — the **one** place a
 * stored value becomes the number the engine predicate is evaluated against.
 *
 * ── Why the parse lives here and not in `more/` ───────────────────────────────
 *
 * Two consumers: the More tab writes and displays it, the capture chrome's notification strip
 * evaluates the nudge against it. A shared mechanism that lives inside one of its consumers
 * invites the second consumer to make a copy — the U-9b finding that moved the DISC-7 quiet gate
 * out of `domain/discovery.ts`. So it sits beside `theme.ts`, the other setting both a screen and
 * the app read, at the same level and for the same reason.
 *
 * ── One derivation, so the screen and the nudge cannot disagree ───────────────
 *
 * The More tab renders {@link lockNudgeMinFromB}'s answer, not the raw stored value. That matters
 * for exactly the case the fallback exists for: a row holding `999` would otherwise render as
 * *"999"* on the settings screen while the nudge quietly used `LOCK_NUDGE_MIN`, and the user would
 * be reading a number the product is not using.
 *
 * ── Why the engine does not validate this, and this does ──────────────────────
 *
 * `SettingRow.value` is `unknown` by the engine's own contract (`commands/settings.ts`: the closed
 * *key* space is the engine's business, the value vocabulary belongs to the consuming surface),
 * and the settings table has a **second writer** — an archive import, which writes rows straight
 * from a file this device did not produce and `lockNudgeMin` is an archived key (§11.8). So the
 * band has to be enforced at the read, where both writers' output converges, rather than at the
 * one writer that happens to have a UI in front of it.
 */

const MODULE = 'ui-b/lockNudge';

/**
 * The minutes B's More tab offers, derived from the bounds at `LOCK_NUDGE_STEP_MIN` rather than
 * listed — so the offered choices and the accepted band cannot drift apart. `validate.ts` proves
 * the default lands on a step, so this list always contains `config.LOCK_NUDGE_MIN`.
 */
export const LOCK_NUDGE_CHOICES_B: readonly number[] = (() => {
  const out: number[] = [];
  for (
    let minute = config.LOCK_NUDGE_MIN_MIN;
    minute <= config.LOCK_NUDGE_MIN_MAX;
    minute += config.LOCK_NUDGE_STEP_MIN
  ) {
    out.push(minute);
  }
  return out;
})();

/**
 * A stored setting value read as a lock-nudge minute — **total**, and deny-by-default.
 *
 * Anything that is not a whole number inside `[LOCK_NUDGE_MIN_MIN, LOCK_NUDGE_MIN_MAX]` reads as
 * `config.LOCK_NUDGE_MIN`: absent (never set, or wiped by an import), the wrong type, a fraction,
 * `NaN`, or a minute outside the band. **Falling back, not clamping** — a clamp would invent a
 * threshold the user never chose and present it as theirs; the default is the one value the
 * product can honestly attribute to nobody.
 *
 * The fallback is the constant **by reference**, which is what keeps this parse and A's untouched
 * `lockNudgeDue()` call agreeing about what "default" means without either restating a number.
 */
export function lockNudgeMinFromB(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) return config.LOCK_NUDGE_MIN;
  if (value < config.LOCK_NUDGE_MIN_MIN || value > config.LOCK_NUDGE_MIN_MAX) {
    return config.LOCK_NUDGE_MIN;
  }
  return value;
}

/**
 * Reads the stored minute, or the default. **Never rejects** — a settings read that failed must
 * not be able to eject a scout from a live match.
 *
 * The takeover host awaits this inside its own load gate, so the strip's first evaluation already
 * has the user's number. A hook that returned the default first and corrected on the next frame
 * would flash the offer at the wrong minute on a resume past 15′ with the setting at 20′ — for a
 * theme that is a cosmetic flicker (`theme.ts` names its own), for a prompt it is a wrong prompt.
 */
export async function readLockNudgeMinB(): Promise<number> {
  try {
    return lockNudgeMinFromB((await loadSettings()).lockNudgeMin);
  } catch (cause) {
    log.error(MODULE, 'LOCK-1: the lock-nudge minute could not be read; using the default', {
      message: cause instanceof Error ? cause.message : String(cause),
    });
    return config.LOCK_NUDGE_MIN;
  }
}
